import { useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { InkDocumentV1, InkPointerType, InkStroke } from '../../types/ink';
import { normalizeInkPoint } from '../../lib/ink/normalize';

type Props = {
  ariaLabel?: string;
  height?: number;
  onChange?: (document: InkDocumentV1) => void;
};

function pointerType(value: string): InkPointerType {
  if (value === 'pen' || value === 'touch' || value === 'mouse') return value;
  return 'unknown';
}

export function InkPad({ ariaLabel = 'Area menulis jawaban', height = 220, onChange }: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [strokes, setStrokes] = useState<InkStroke[]>([]);
  const activeStroke = useRef<InkStroke | null>(null);
  const activePenPointer = useRef<number | null>(null);
  const startedAt = useRef<number>(performance.now());


  useEffect(() => {
    if (!onChange) return;
    const normalized = strokes.map((stroke) => ({
      ...stroke,
      points: stroke.points.map((point) => normalizeInkPoint(point.x, point.y, point.t, 700, height, point.pressure)),
    }));
    onChange({ version: 1, width: 700, height, strokes: normalized });
  }, [height, onChange, strokes]);

  const paths = useMemo(() => strokes.map((stroke) => ({
    id: stroke.id,
    d: stroke.points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' '),
  })), [strokes]);

  function pointFromEvent(event: ReactPointerEvent<SVGSVGElement>) {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: Math.max(0, Math.min(700, ((event.clientX - rect.left) / rect.width) * 700)),
      y: Math.max(0, Math.min(height, ((event.clientY - rect.top) / rect.height) * height)),
      t: Math.round(performance.now() - startedAt.current),
      pressure: Number.isFinite(event.pressure) ? event.pressure : undefined,
    };
  }

  function onPointerDown(event: ReactPointerEvent<SVGSVGElement>) {
    if (event.pointerType === 'touch' && activePenPointer.current !== null) return;
    if (event.pointerType === 'pen') activePenPointer.current = event.pointerId;
    const point = pointFromEvent(event);
    if (!point) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    activeStroke.current = {
      id: `${Date.now()}-${event.pointerId}-${Math.random().toString(16).slice(2)}`,
      pointerType: pointerType(event.pointerType),
      points: [point],
    };
  }

  function onPointerMove(event: ReactPointerEvent<SVGSVGElement>) {
    if (!activeStroke.current) return;
    if (event.pointerType === 'touch' && activePenPointer.current !== null) return;
    const point = pointFromEvent(event);
    if (!point) return;
    const previous = activeStroke.current.points.at(-1);
    if (previous) {
      const distance = Math.hypot(point.x - previous.x, point.y - previous.y);
      const deltaTime = point.t - previous.t;
      if (distance < 2.5 && deltaTime < 12) return;
    }
    if (activeStroke.current.points.length >= 5000) return;
    activeStroke.current = { ...activeStroke.current, points: [...activeStroke.current.points, point] };
    setStrokes((current) => {
      const withoutActive = current.filter((stroke) => stroke.id !== activeStroke.current?.id);
      return activeStroke.current ? [...withoutActive, activeStroke.current] : current;
    });
  }

  function finishPointer(event: ReactPointerEvent<SVGSVGElement>) {
    if (event.pointerType === 'pen' && activePenPointer.current === event.pointerId) activePenPointer.current = null;
    activeStroke.current = null;
  }

  return (
    <div className="ink-pad">
      <div className="ink-pad__toolbar no-print">
        <span className="muted">Tulis seperti di worksheet</span>
        <div className="ink-pad__actions">
          <button className="btn btn--small" type="button" onClick={() => setStrokes((current) => current.slice(0, -1))} disabled={strokes.length === 0}>Undo</button>
          <button className="btn btn--small" type="button" onClick={() => setStrokes([])} disabled={strokes.length === 0}>Hapus</button>
        </div>
      </div>
      <svg
        ref={svgRef}
        className="ink-pad__canvas"
        viewBox={`0 0 700 ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishPointer}
        onPointerCancel={finishPointer}
        onPointerLeave={(event) => {
          if (event.buttons === 0) finishPointer(event);
        }}
      >
        <rect x="0" y="0" width="700" height={height} className="ink-pad__paper" />
        {[1, 2, 3].map((line) => <line key={line} x1="0" x2="700" y1={(height / 4) * line} y2={(height / 4) * line} className="ink-pad__guide" />)}
        {paths.map((path) => <path key={path.id} d={path.d} className="ink-pad__stroke" vectorEffect="non-scaling-stroke" />)}
      </svg>
      <p className="ink-pad__caption muted">Pilot v0.3 menyimpan jawaban terstruktur untuk penilaian. Tulisan stylus/jari sudah dapat dicoba, tetapi pengenalan tulisan otomatis belum diaktifkan.</p>
    </div>
  );
}
